/**
 * Subscription display state (PWA).
 *
 * `availabledb.expiry` on each outlet server is the single source of truth for
 * database hosting; the gateway subscription (`gw_subscriptions.expires_at`)
 * is a separate, independent value. Both are rendered with the same rules:
 * amber inside EXPIRING_DAYS, red once expired.
 *
 * Day arithmetic mirrors the gateway (`BillingTime::DaysBetween`): calendar
 * days in UTC+8, so the PWA and the server flip status on the same midnight.
 */

export type SubscriptionStatus =
  | 'active'
  | 'expiring'
  | 'expired'
  | 'not_subscribed'
  | 'no_expiry'
  | 'not_applicable'
  | 'unknown';

export interface SubscriptionState {
  status: SubscriptionStatus;
  daysLeft: number;
}

export interface OutletSubscriptionInfo {
  db: {
    enforced: boolean;
    forever: boolean;
    subscribed: boolean;
    expiresAt: number;
    daysLeft: number;
    status: SubscriptionStatus;
  };
  gateway: {
    subscribed: boolean;
    expiresAt: number;
    daysLeft: number;
    status: SubscriptionStatus;
  };
}

/** Days before expiry that switch the UI to the warning colour. */
export const EXPIRING_DAYS = 7;

/** Anything past 2100-01-01 is "never expires" (same cutoff as the gateway). */
export const FOREVER_CUTOFF_MS = 4102444800000;

export function isForeverMs(ms?: number | null): boolean {
  return typeof ms === 'number' && (ms < 0 || ms > FOREVER_CUTOFF_MS);
}

/** Calendar days from `fromMs` to `toMs` in UTC+8 (negative when past). */
export function daysBetweenUtc8(fromMs: number, toMs: number): number {
  const day = 86_400_000;
  const shift = 8 * 3_600_000; // Asia/Kuala_Lumpur
  return Math.floor((toMs + shift) / day) - Math.floor((fromMs + shift) / day);
}

/**
 * Compute the display state from an absolute epoch-ms expiry, so the caller
 * can re-evaluate it locally (a 60 s tick is enough to flip at midnight)
 * without another network round-trip.
 */
export function subscriptionState(
  expiresAtMs: number | undefined | null,
  opts: { enforced?: boolean; forever?: boolean } = {},
  nowMs: number = Date.now(),
): SubscriptionState {
  if (opts.enforced === false) return { status: 'not_applicable', daysLeft: 0 };
  if (opts.forever || isForeverMs(expiresAtMs)) return { status: 'no_expiry', daysLeft: 0 };
  if (!expiresAtMs || expiresAtMs <= 0) return { status: 'not_subscribed', daysLeft: 0 };
  const daysLeft = daysBetweenUtc8(nowMs, expiresAtMs);
  if (daysLeft < 0) return { status: 'expired', daysLeft };
  if (daysLeft <= EXPIRING_DAYS) return { status: 'expiring', daysLeft };
  return { status: 'active', daysLeft };
}

export function statusLabel(status: SubscriptionStatus): string {
  switch (status) {
    case 'active': return 'Active';
    case 'expiring': return 'Expiring soon';
    case 'expired': return 'Expired';
    case 'not_subscribed': return 'Not subscribed';
    case 'no_expiry': return 'No expiry';
    case 'not_applicable': return 'Not applicable';
    default: return 'Unknown';
  }
}

/** Short text for the outlet tree badge, e.g. "5d" / "expired". */
export function shortDaysText(state: SubscriptionState): string {
  if (state.status === 'expired') return 'expired';
  if (state.status === 'expiring' || state.status === 'active') return `${state.daysLeft}d`;
  return '';
}

/** Human date from epoch MILLISECONDS (the ownedDBs subscription blocks). */
export function formatExpiryDateMs(epochMs: number): string {
  if (!epochMs || epochMs <= 0) return '—';
  return new Date(epochMs).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
